// =====================================================================
// PropertyDetailCharts.jsx
// =====================================================================
// All 4 trend charts for /operational/:location — Monthly Revenue/Expense/Occupancy,
// ADR/RevPAB, YoY occupancy across FYs, Revenue Mix (Bed vs F&B), and Cost-per-bed.
// Extracted from PropertyDetail.jsx (iter-35e split).
// =====================================================================

import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ComposedChart,
} from "recharts";

// Chart-prop constants — extracted to module scope (recharts doesn't see a fresh
// object per render). Preserved from PropertyDetail.jsx iter-35d.
const TICK_STYLE = { fontSize: 11, fontFamily: "JetBrains Mono", fill: "#71717A" };
const TICK_STYLE_SM = { fontSize: 10, fontFamily: "JetBrains Mono", fill: "#71717A" };
const TOOLTIP_STYLE = { background: "#09090B", border: "none", color: "#fff" };
const DOT_R3 = { r: 3 };
const DOT_R2 = { r: 2 };
const OCC_DOMAIN = [0, 100];

// ── 1) Monthly composed chart — Revenue + Expense (bars) + Occupancy (line) ──
export function MonthlyPerformanceChart({ months, activeFY }) {
  const chartData = months.map((m) => ({
    ...m,
    rev_l: Math.round(m.total_revenue / 100000),
    exp_l: Math.round(m.total_expense / 100000),
  }));
  return (
    <div className="bg-white border border-brand-line p-6 mb-4" data-testid="monthly-chart">
      <div className="flex justify-between mb-4">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">Monthly performance · {activeFY}</div>
          <h3 className="font-display font-bold text-lg mt-1">Revenue, expense & occupancy</h3>
        </div>
        <div className="flex gap-3 font-mono-tech text-[10px] uppercase tracking-[0.2em]">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-brand-yellow"></span> Revenue</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-red-400"></span> Expense</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-brand-ink"></span> Occupancy %</span>
        </div>
      </div>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData}>
            <CartesianGrid stroke="#E4E4E7" vertical={false} />
            <XAxis dataKey="month_label" tick={TICK_STYLE} axisLine={false} tickLine={false} />
            <YAxis yAxisId="rev" tick={TICK_STYLE} axisLine={false} tickLine={false} />
            <YAxis yAxisId="occ" orientation="right" domain={OCC_DOMAIN} tick={TICK_STYLE} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Bar yAxisId="rev" dataKey="rev_l" fill="#FFCC00" name="Revenue (₹L)" />
            <Bar yAxisId="rev" dataKey="exp_l" fill="#F87171" name="Expense (₹L)" />
            <Line yAxisId="occ" type="monotone" dataKey="occupancy_pct" stroke="#09090B" strokeWidth={2.5} dot={DOT_R3} name="Occupancy %" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ── 2 & 3) ADR & RevPAB (left) + YoY occupancy (right) ──
export function AdrYoyDuoChart({ months, activeFY, yoyData, fys, fyColors }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
      <div className="bg-white border border-brand-line p-6" data-testid="adr-chart">
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">ADR & RevPAB</div>
        <h3 className="font-display font-bold text-lg mt-1 mb-4">{activeFY} pricing power</h3>
        <div className="h-60">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={months}>
              <CartesianGrid stroke="#E4E4E7" vertical={false} />
              <XAxis dataKey="month_label" tick={TICK_STYLE_SM} axisLine={false} tickLine={false} />
              <YAxis tick={TICK_STYLE} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Line type="monotone" dataKey="adr" stroke="#F97316" strokeWidth={2.5} name="ADR" dot={DOT_R3} />
              <Line type="monotone" dataKey="revpab" stroke="#0EA5E9" strokeWidth={2.5} name="RevPAB" dot={DOT_R3} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-brand-line p-6" data-testid="yoy-chart">
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">YoY · Occupancy</div>
        <h3 className="font-display font-bold text-lg mt-1 mb-4">All FYs Apr → Mar</h3>
        <div className="h-60">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={yoyData}>
              <CartesianGrid stroke="#E4E4E7" vertical={false} />
              <XAxis dataKey="month" tick={TICK_STYLE} axisLine={false} tickLine={false} />
              <YAxis domain={OCC_DOMAIN} tick={TICK_STYLE} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              {fys.map((fy) => (
                <Line
                  key={fy}
                  type="monotone"
                  dataKey={fy}
                  stroke={fyColors[fy]}
                  strokeWidth={2}
                  dot={DOT_R2}
                  connectNulls
                  name={fy}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ── 4 & 5) Revenue Mix (Bed vs F&B) + Cost per Bed ──
export function RevenueMixCostBedChart({ months, activeFY }) {
  const revMixData = months.map((m) => ({
    ...m,
    bed_l: Math.round(m.bed_revenue / 100000),
    fb_l: Math.round(m.fb_revenue / 100000),
  }));
  const costPerBedData = months.map((m) => ({
    month_label: m.month_label,
    rent_pb: m.capacity_beds ? Math.round(m.rent / m.capacity_beds) : 0,
    salary_pb: m.capacity_beds ? Math.round(m.salary / m.capacity_beds) : 0,
    other_pb: m.capacity_beds ? Math.round(Math.max(0, (m.total_expense - m.rent - m.salary)) / m.capacity_beds) : 0,
  }));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
      <div className="bg-white border border-brand-line p-6" data-testid="revenue-mix-chart">
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">Revenue mix</div>
        <h3 className="font-display font-bold text-lg mt-1 mb-4">{activeFY} bed revenue vs F&B</h3>
        <div className="h-60">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={revMixData} stackOffset="sign">
              <CartesianGrid stroke="#E4E4E7" vertical={false} />
              <XAxis dataKey="month_label" tick={TICK_STYLE_SM} axisLine={false} tickLine={false} />
              <YAxis tick={TICK_STYLE} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="bed_l" stackId="rev" fill="#FFCC00" name="Bed Revenue (₹L)" />
              <Bar dataKey="fb_l" stackId="rev" fill="#F97316" name="F&B Revenue (₹L)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-brand-line p-6" data-testid="cost-per-bed-chart">
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">Cost per capacity bed</div>
        <h3 className="font-display font-bold text-lg mt-1 mb-4">{activeFY} efficiency view</h3>
        <div className="h-60">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={costPerBedData}>
              <CartesianGrid stroke="#E4E4E7" vertical={false} />
              <XAxis dataKey="month_label" tick={TICK_STYLE_SM} axisLine={false} tickLine={false} />
              <YAxis tick={TICK_STYLE} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="rent_pb" stackId="cost" fill="#0EA5E9" name="Rent / bed" />
              <Bar dataKey="salary_pb" stackId="cost" fill="#A855F7" name="Salary / bed" />
              <Bar dataKey="other_pb" stackId="cost" fill="#71717A" name="Other / bed" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ── 6) YoY growth bar — Activeyear vs PrevFY across 4 metrics ──
const YOY_MARGIN = { top: 20 };

export function YoyGrowthChart({ summary, activeFY, fys }) {
  const fyIdx = fys.indexOf(activeFY);
  const prevFy = fyIdx > 0 ? fys[fyIdx - 1] : null;
  const cur = summary.find((f) => f.fy === activeFY);
  const prev = prevFy ? summary.find((f) => f.fy === prevFy) : null;
  if (!prev || !cur) return null;
  const metrics = [
    { key: "avg_occupancy_pct", label: "Occupancy %" },
    { key: "avg_adr", label: "ADR" },
    { key: "avg_revpab", label: "RevPAB" },
    { key: "total_revenue_lakhs", label: "Revenue (₹L)" },
  ];
  const yoyData = metrics.map((m) => ({
    metric: m.label,
    [prevFy]: prev[m.key],
    [activeFY]: cur[m.key],
    delta: prev[m.key] ? Math.round(((cur[m.key] - prev[m.key]) / prev[m.key]) * 100) : 0,
  }));
  return (
    <div className="bg-white border border-brand-line p-6 mt-4" data-testid="yoy-bar">
      <div className="flex justify-between mb-4">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">Growth vs LY</div>
          <h3 className="font-display font-bold text-lg mt-1">{activeFY} vs {prevFy}</h3>
        </div>
        <div className="flex gap-3 font-mono-tech text-[10px] uppercase tracking-[0.2em]">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-zinc-400"></span> {prevFy} (LY)</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-brand-yellow"></span> {activeFY}</span>
        </div>
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={yoyData} margin={YOY_MARGIN}>
            <CartesianGrid stroke="#E4E4E7" vertical={false} />
            <XAxis dataKey="metric" tick={TICK_STYLE} axisLine={false} tickLine={false} />
            <YAxis tick={TICK_STYLE} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Bar dataKey={prevFy} fill="#A1A1AA" />
            <Bar dataKey={activeFY} fill="#FFCC00" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-brand-line">
        {yoyData.map((m) => (
          <div key={m.metric}>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">{m.metric}</div>
            <div className={`font-display font-black text-2xl ${m.delta >= 0 ? "text-emerald-600" : "text-red-600"}`}>
              {m.delta >= 0 ? "+" : ""}{m.delta}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
